// User roles in order of hierarchy (higher index = more permissions)
export enum UserRole {
  PROVIDER_GROUP_ADMIN = 'ProviderGroupAdmin',
  PROVIDER_ADMIN = 'ProviderAdmin', 
  SUPER_USER = 'SuperUser'
}

export enum BackendAuthorityRole {
  SUPER_USER = 'SuperUser',
  PROVIDER_ADMIN = 'ProviderAdminUser',
  PROVIDER_GROUP_ADMIN = 'ProviderGroupAdminUser',
}

// Permission levels for granular access control
export enum Permission {
  // Basic permissions
  VIEW_DASHBOARD = 'view:dashboard',
  VIEW_PROFILE = 'view:profile',
  EDIT_PROFILE = 'edit:profile',
  
  // Provider Group permissions
  VIEW_PROVIDER_GROUPS = 'view:provider-groups',
  CREATE_PROVIDER_GROUPS = 'create:provider-groups',
  EDIT_PROVIDER_GROUPS = 'edit:provider-groups',
  DELETE_PROVIDER_GROUPS = 'delete:provider-groups',
  
  // Provider permissions
  VIEW_PROVIDERS = 'view:providers',
  CREATE_PROVIDERS = 'create:providers',
  EDIT_PROVIDERS = 'edit:providers',
  DELETE_PROVIDERS = 'delete:providers',
  
  // Provider User permissions
  VIEW_PROVIDER_USERS = 'view:provider-users',
  CREATE_PROVIDER_USERS = 'create:provider-users',
  EDIT_PROVIDER_USERS = 'edit:provider-users',
  DELETE_PROVIDER_USERS = 'delete:provider-users',
  
  // Provider Client permissions
  VIEW_PROVIDER_CLIENTS = 'view:provider-clients',
  CREATE_PROVIDER_CLIENTS = 'create:provider-clients',
  EDIT_PROVIDER_CLIENTS = 'edit:provider-clients',
  DELETE_PROVIDER_CLIENTS = 'delete:provider-clients',
  
  // Admin permissions
  VIEW_ADMIN_PANEL = 'view:admin-panel',
  MANAGE_USERS = 'manage:users',
  MANAGE_SYSTEM = 'manage:system',
  
  // Vendor permissions
  VIEW_VENDORS = 'view:vendors',
  MANAGE_VENDORS = 'manage:vendors',
  
  // Item permissions
  VIEW_ITEMS = 'view:items',
  MANAGE_ITEMS = 'manage:items',
}

// Role to permissions mapping
const providerGroupAdminPermissions = [
  Permission.VIEW_DASHBOARD,
  Permission.VIEW_PROFILE,
  Permission.EDIT_PROFILE,
  Permission.VIEW_PROVIDER_GROUPS,
  Permission.CREATE_PROVIDER_GROUPS,
  Permission.EDIT_PROVIDER_GROUPS,
  Permission.DELETE_PROVIDER_GROUPS,
]

const providerAdminPermissions = [
  ...providerGroupAdminPermissions,
  Permission.VIEW_PROVIDERS,
  Permission.CREATE_PROVIDERS,
  Permission.EDIT_PROVIDERS,
  Permission.DELETE_PROVIDERS,
  Permission.VIEW_PROVIDER_USERS,
  Permission.CREATE_PROVIDER_USERS,
  Permission.EDIT_PROVIDER_USERS,
  Permission.DELETE_PROVIDER_USERS,
  Permission.VIEW_PROVIDER_CLIENTS,
  Permission.CREATE_PROVIDER_CLIENTS,
  Permission.EDIT_PROVIDER_CLIENTS,
  Permission.DELETE_PROVIDER_CLIENTS,
  Permission.VIEW_VENDORS,
  Permission.VIEW_ITEMS,
]

const superUserPermissions = [
  ...providerAdminPermissions,
  Permission.VIEW_ADMIN_PANEL,
  Permission.MANAGE_USERS,
  Permission.MANAGE_SYSTEM,
  Permission.MANAGE_VENDORS,
  Permission.MANAGE_ITEMS,
]

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.PROVIDER_GROUP_ADMIN]: providerGroupAdminPermissions,
  [UserRole.PROVIDER_ADMIN]: providerAdminPermissions,
  [UserRole.SUPER_USER]: superUserPermissions,
}

// Helper functions
export const hasPermission = (userRole: UserRole | undefined, permission: Permission): boolean => {
  if (!userRole) return false
  return ROLE_PERMISSIONS[userRole]?.includes(permission) ?? false
}

export const hasAnyPermission = (userRole: UserRole | undefined, permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(userRole, permission))
}

export const hasAllPermissions = (userRole: UserRole | undefined, permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(userRole, permission))
}

export const getRoleHierarchyLevel = (role: UserRole): number => {
  const roles = Object.values(UserRole)
  return roles.indexOf(role)
}

export const hasHigherOrEqualRole = (userRole: UserRole | undefined, requiredRole: UserRole): boolean => {
  if (!userRole) return false
  return getRoleHierarchyLevel(userRole) >= getRoleHierarchyLevel(requiredRole)
}

export const resolveUserRole = ({
  role,
  roles = [],
}: {
  role?: string | null
  roles?: string[]
}): UserRole | undefined => {
  const allRoles = new Set<string>([...(role ? [role] : []), ...roles])

  if (allRoles.has(BackendAuthorityRole.SUPER_USER) || allRoles.has(UserRole.SUPER_USER)) {
    return UserRole.SUPER_USER
  }

  if (allRoles.has(BackendAuthorityRole.PROVIDER_ADMIN) || allRoles.has(UserRole.PROVIDER_ADMIN)) {
    return UserRole.PROVIDER_ADMIN
  }

  if (
    allRoles.has(BackendAuthorityRole.PROVIDER_GROUP_ADMIN) ||
    allRoles.has(UserRole.PROVIDER_GROUP_ADMIN)
  ) {
    return UserRole.PROVIDER_GROUP_ADMIN
  }

  return undefined
}
